import type { ChartOptionFieldDef, ChartOptionPanelId } from "../../utils/chartOptionRegistry";
import { GRID_FULL, GRID_HALF, GRID_THIRD } from "../../lib/ui/formLayout";

export type PlacementH = "left" | "center" | "right";
export type PlacementV = "top" | "middle" | "bottom";

export type GridPaddingPresetId = "compact" | "balanced" | "spacious" | "custom";

export type GridPaddingValues = {
  left: string;
  right: string;
  top: string;
  bottom: string;
};

export const PLACEMENT_HORIZONTAL: PlacementH[] = ["left", "center", "right"];
export const PLACEMENT_VERTICAL: PlacementV[] = ["top", "middle", "bottom"];

export const PLACEMENT_FIELD_IDS = new Set([
  "titleLeft",
  "titleTop",
  "legendLeft",
  "legendTop",
]);

export const GRID_PADDING_FIELD_IDS = new Set(["gridLeft", "gridRight", "gridTop", "gridBottom"]);

export const GRID_PADDING_PRESETS: Record<Exclude<GridPaddingPresetId, "custom">, GridPaddingValues> = {
  compact: { left: "8%", right: "4%", top: "12%", bottom: "8%" },
  balanced: { left: "10%", right: "6%", top: "16%", bottom: "10%" },
  spacious: { left: "14%", right: "10%", top: "20%", bottom: "14%" },
};

const AXES_X_FIELDS = new Set(["xAxisName", "xAxisLabelRotate", "xAxisLabelShow", "xAxisSplitLine"]);
const AXES_Y_FIELDS = new Set(["yAxisName", "yAxisMin", "yAxisMax", "yAxisSplitLine"]);

const TITLE_LEGEND_GROUPS: Record<string, string[]> = {
  title: ["titleText", "titleSubtext"],
  legend: ["legendShow", "legendOrient", "legendType"],
};

export function isCompositeField(fieldId: string): boolean {
  return PLACEMENT_FIELD_IDS.has(fieldId) || GRID_PADDING_FIELD_IDS.has(fieldId);
}

export function filterRenderableFields(fields: ChartOptionFieldDef[]): ChartOptionFieldDef[] {
  return fields.filter((field) => !isCompositeField(field.id));
}

export function groupFieldsForPanel(
  panel: ChartOptionPanelId,
  fields: ChartOptionFieldDef[]
): Array<{ id: string; labelKey?: string; fields: ChartOptionFieldDef[] }> {
  const renderable = filterRenderableFields(fields);

  if (panel === "titleLegend") {
    return [
      {
        id: "title",
        labelKey: "chartOptions.sections.title",
        fields: renderable.filter((field) => TITLE_LEGEND_GROUPS.title.includes(field.id)),
      },
      {
        id: "legend",
        labelKey: "chartOptions.sections.legend",
        fields: renderable.filter((field) => TITLE_LEGEND_GROUPS.legend.includes(field.id)),
      },
    ].filter((group) => group.fields.length > 0);
  }

  if (panel === "axes") {
    return [
      {
        id: "xAxis",
        labelKey: "chartOptions.sections.xAxis",
        fields: renderable.filter((field) => AXES_X_FIELDS.has(field.id)),
      },
      {
        id: "yAxis",
        labelKey: "chartOptions.sections.yAxis",
        fields: renderable.filter((field) => AXES_Y_FIELDS.has(field.id)),
      },
    ].filter((group) => group.fields.length > 0);
  }

  if (renderable.length === 0) {
    return [];
  }

  return [{ id: "default", fields: renderable }];
}

export function getCompactFieldSize(field: ChartOptionFieldDef): {
  xs: number;
  sm?: number;
  md?: number;
} {
  if (field.id === "titleText" || field.id === "titleSubtext") return GRID_HALF;
  if (field.type === "text") return GRID_FULL;
  if (field.type === "boolean" || field.type === "color" || field.type === "number") {
    return { xs: 12, sm: 6, md: 4 };
  }
  if (field.type === "select") return GRID_HALF;
  return GRID_THIRD;
}

export function fieldGridSpan(field: ChartOptionFieldDef): 1 | 2 {
  if (field.type === "boolean") return 1;
  if (field.type === "text") return 2;
  if (field.type === "color") return 2;
  return 1;
}

export function normalizePlacementH(value: unknown): PlacementH {
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "left" || raw === "center" || raw === "right") return raw;
  return "center";
}

export function normalizePlacementV(value: unknown): PlacementV {
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "top" || raw === "middle" || raw === "bottom") return raw;
  return "top";
}

export function normalizePlacement(value: unknown, axis: "h" | "v"): PlacementH | PlacementV | undefined {
  if (axis === "h") return normalizePlacementH(value);
  return normalizePlacementV(value);
}

export function parseGridMargin(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const match = raw.match(/^(-?\d+(?:\.\d+)?)/);
  if (!match) return 0;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : 0;
}

export function formatGridMargin(value: number): string {
  const clamped = Math.max(0, Math.min(40, Math.round(value)));
  return `${clamped}%`;
}

export function readGridPadding(values: Partial<GridPaddingValues>): GridPaddingValues {
  return {
    left: values.left ?? GRID_PADDING_PRESETS.balanced.left,
    right: values.right ?? GRID_PADDING_PRESETS.balanced.right,
    top: values.top ?? GRID_PADDING_PRESETS.balanced.top,
    bottom: values.bottom ?? GRID_PADDING_PRESETS.balanced.bottom,
  };
}

export function detectGridPaddingPreset(values: GridPaddingValues): GridPaddingPresetId {
  for (const [presetId, preset] of Object.entries(GRID_PADDING_PRESETS) as Array<
    [Exclude<GridPaddingPresetId, "custom">, GridPaddingValues]
  >) {
    const matches = (["left", "right", "top", "bottom"] as const).every(
      (side) => parseGridMargin(values[side]) === parseGridMargin(preset[side])
    );
    if (matches) return presetId;
  }
  return "custom";
}
